/**
 * Sticky section navigation for the public memorial page.
 */
import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export function MemorialPublicNav({ tabs, activeId, onSelect, className }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current?.querySelector(`[data-tab-id="${activeId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeId]);

  if (!tabs?.length) return null;

  return (
    <nav
      className={cn(
        'memorial-public__nav',
        className,
      )}
      aria-label="Memorial sections"
      data-testid="memorial-public-nav"
    >
      <div className="mx-auto max-w-[var(--memorial-content-max,72rem)] px-4 sm:px-8">
        <div className="py-2 sm:hidden">
          <label className="sr-only" htmlFor="memorial-section-select">Jump to section</label>
          <div className="relative">
            <select
              id="memorial-section-select"
              value={activeId}
              onChange={(e) => onSelect(e.target.value)}
              className="h-10 w-full appearance-none rounded-full border border-[hsl(38_14%_82%)] bg-white pl-4 pr-10 text-sm text-[hsl(24_14%_18%)]"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>{tab.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>

        <div
          ref={scrollRef}
          className="hidden gap-1.5 overflow-x-auto py-3 sm:flex sm:snap-x sm:snap-mandatory"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-tab-id={tab.id}
              aria-selected={activeId === tab.id}
              onClick={() => onSelect(tab.id)}
              className={cn(
                'shrink-0 snap-start rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-colors',
                activeId === tab.id
                  ? 'memorial-public__nav-tab memorial-public__nav-tab--active'
                  : 'memorial-public__nav-tab memorial-public__nav-tab--idle',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

export default MemorialPublicNav;
